import { Component, computed, inject } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import {
  Button,
  InputLabel,
  Dropzone,
  Alert,
  DropzoneFile,
  NotificationService,
  ConfirmService,
  InputError,
} from 'excellence-ui';
import { ActivatedRoute, Router } from '@angular/router';
import { ParseRequestCreatePage } from '../parse-request-create-page/parse-request-create-page';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ParseRequestsStore } from '../../stores/parse-requests.store';
import { DecimalPipe } from '@angular/common';
import { ParseRequestCreateAccordion } from '../../components/parse-request-create-accordion/parse-request-create-accordion';

@Component({
  selector: 'app-parse-request-create-documents-page',
  imports: [
    NgIcon,
    Button,
    TranslocoPipe,
    InputLabel,
    Dropzone,
    Alert,
    DropzoneFile,
    DecimalPipe,
    ParseRequestCreateAccordion,
    InputError,
  ],
  templateUrl: './parse-request-create-documents-page.html',
})
export class ParseRequestCreateDocumentsPage {
  private router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  private parseRequestsStore = inject(ParseRequestsStore);
  private notificationService = inject(NotificationService);
  private confirmService = inject(ConfirmService);
  private translocoService = inject(TranslocoService);
  private parent = inject(ParseRequestCreatePage);

  protected state = this.parent.state;

  protected outstandingFiles = computed(() => this.state()?.outstandingFiles ?? []);
  protected uploadedFiles = computed(() => this.state()?.data?.files ?? []);
  protected hasUploadedFiles = computed(() => {
    const outstandingFiles = this.outstandingFiles();
    return (
      (this.uploadedFiles().length > 0 || outstandingFiles.length > 0) &&
      outstandingFiles.every((file) => file.uploadedFileId !== undefined)
    );
  });

  protected isUpdateLoading = computed(() => !!this.state()?.isUpdateLoading);
  protected updateError = computed(() => this.state()?.updateError ?? null);

  protected onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.onFileDrop(files);
    }
  }

  protected async onFileDrop(files: FileList) {
    const id = this.state()?.id;
    if (!id) {
      return;
    }
    this.parseRequestsStore.uploadDocuments(id, files);
  }

  protected async onClickFileDelete(fileId: string) {
    const state = this.state();
    if (!state) {
      throw new Error('State is required to delete file');
    }
    const confirmed = await this.confirmService.confirm({
      mode: 'destructive',
      title: this.translocoService.translate(
        'cockpit.parseRequests.createDocuments.deleteDialog.title',
      ),
      message: this.translocoService.translate(
        'cockpit.parseRequests.createDocuments.deleteDialog.message',
      ),
      confirmButtonText: this.translocoService.translate(
        'cockpit.parseRequests.createDocuments.deleteDialog.confirmButton',
      ),
      cancelButtonText: this.translocoService.translate(
        'cockpit.parseRequests.createDocuments.deleteDialog.cancelButton',
      ),
    });
    if (!confirmed) {
      return;
    }
    try {
      await this.parseRequestsStore.deleteFile(state.id, fileId);
      if (!this.hasUploadedFiles()) {
        await this.parseRequestsStore.update(state.id, {
          documentUploadState: 'IN_PROGRESS',
        });
      }
    } catch (error) {
      this.notificationService.showError({
        title: this.translocoService.translate(
          'cockpit.parseRequests.createDocuments.deleteError.title',
        ),
        subtitle: this.translocoService.translate(
          'cockpit.parseRequests.createDocuments.deleteError.subtitle',
        ),
      });
      throw error;
    }
  }

  protected onClickRetryFileUpload(fileId: string) {
    const state = this.state();
    if (!state) {
      throw new Error('State is required to retry file upload');
    }
    this.notificationService.showSuccess({
      title: this.translocoService.translate(
        'cockpit.parseRequests.createDocuments.retrySuccess.title',
      ),
    });
    this.parseRequestsStore.retryFileUpload(state.id, fileId);
  }

  protected async onSubmit() {
    const id = this.state()?.id;
    if (!id || !this.hasUploadedFiles()) {
      throw new Error('at least one file must be uploaded');
    }
    await this.parseRequestsStore.reloadItem(id, { quiet: false });
    this.parseRequestsStore.clearOutstandingFiles(id);
    await this.parseRequestsStore.update(id, {
      documentUploadState: 'COMPLETE',
      generalInformationState: 'IN_PROGRESS',
    });
    this.router.navigate(['../general'], {
      relativeTo: this.activatedRoute,
    });
  }
}
